import { baseApi } from "./baseApi";

// RTK Query endpoints for the "Contribute" idea desk. Member intake conversation
// (start / message / voice / submit), the member's own list, the public "shipped
// ideas" for a profile, and the admin review surface. The backend mounts these at
// /api/contribute and /api/admin/contributions; baseUrl already includes "/api".

// ── shared shapes ─────────────────────────────────────────────────────────────

export type ContributionStatus =
  | "draft"
  | "submitted"
  | "triaged"
  | "accepted"
  | "building"
  | "shipped"
  | "declined";

export interface TurnChoice {
  label: string;
  value: string;
}

export interface ChatMessageView {
  role: "assistant" | "user";
  content: string;
  at: string;
  choices?: TurnChoice[];
  // rawTranscript is only present on the admin detail view (the member-facing
  // serializer omits it); kept optional here so both views share this type.
  voice?: { audioUrl: string; durationMs?: number; transcript?: string; rawTranscript?: string };
}

export interface ContributionSummary {
  title: string;
  pitch: string;
}

// the member-facing view of a contribution (no admin brief / feasibility).
export interface UserContribution {
  id: string;
  status: ContributionStatus;
  source: "text" | "voice" | "mixed";
  voiceUsed: boolean;
  title: string;
  pitch: string;
  createdAt: string;
  submittedAt?: string;
  shippedAt?: string;
  credit: { awarded: boolean; respect: number; badgeGranted: boolean };
  shipped: { featureTitle?: string; featureUrl?: string };
  chat?: ChatMessageView[];
}

// one assistant turn in the live intake conversation.
export interface TurnResponse {
  id: string;
  status: ContributionStatus;
  userMessage?: ChatMessageView;
  message: ChatMessageView;
  allowFreeText: boolean;
  readyToSummarize: boolean;
  summary: ContributionSummary | null;
  canSubmit: boolean;
}

export interface ShippedIdea {
  id: string;
  title: string;
  pitch: string;
  shippedAt?: string;
  featureTitle?: string;
  featureUrl?: string;
}

// ── admin shapes ──────────────────────────────────────────────────────────────

export interface AdminContributionListItem {
  id: string;
  status: ContributionStatus;
  authorUsername: string;
  title: string;
  category: string;
  feasibility: string | null;
  voiceUsed: boolean;
  createdAt: string;
  submittedAt?: string;
  shippedAt?: string;
  updatedAt: string;
  credited: boolean;
}

export interface ContributionBrief {
  title: string;
  category: string;
  summary: string;
  userValue: string;
  feasibility: "easy" | "moderate" | "ambitious" | "out-of-scope";
  feasibilityNote: string;
  suggestedAreas: string[];
  buildBrief: string;
  openQuestions: string[];
  model: string;
  generatedAt: string;
}

export interface AdminContributionDetail {
  id: string;
  status: ContributionStatus;
  author?: string;
  authorUsername: string;
  source: string;
  voiceUsed: boolean;
  rawPitch: string;
  fitNote: "fits" | "stretch" | "unsure";
  chat: ChatMessageView[];
  summary: ContributionSummary | null;
  brief: ContributionBrief | null;
  adminNotes: string;
  credit: { awarded: boolean; respect: number; badgeGranted: boolean; awardedAt?: string };
  shipped: { featureTitle?: string; featureUrl?: string; shippedAt?: string };
  timestamps: Record<string, string | undefined>;
}

// ── endpoints ─────────────────────────────────────────────────────────────────

export const contributeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // start (or resume) an intake session.
    startContribution: builder.mutation<UserContribution, { fresh?: boolean } | void>({
      query: (body) => ({ url: "/contribute/start", method: "POST", body: body || {} }),
    }),

    // send a typed message, get the next assistant turn.
    sendContributionMessage: builder.mutation<TurnResponse, { id: string; text: string }>({
      query: ({ id, text }) => ({ url: `/contribute/${id}/message`, method: "POST", body: { text } }),
    }),

    // send a voice pitch/answer (multipart), get the next assistant turn.
    sendContributionVoice: builder.mutation<TurnResponse, { id: string; audio: Blob; durationMs: number }>({
      query: ({ id, audio, durationMs }) => {
        const fd = new FormData();
        fd.append("audio", audio, "pitch.webm");
        fd.append("durationMs", String(durationMs));
        return { url: `/contribute/${id}/voice`, method: "POST", body: fd };
      },
    }),

    // confirm & submit (optionally with an edited summary).
    submitContribution: builder.mutation<UserContribution, { id: string; title?: string; pitch?: string }>({
      query: ({ id, ...body }) => ({ url: `/contribute/${id}/submit`, method: "POST", body }),
      invalidatesTags: ["Contributions"],
    }),

    // the member's own contributions (drafts + submitted).
    getMyContributions: builder.query<{ contributions: UserContribution[] }, void>({
      query: () => "/contribute/mine",
      providesTags: ["Contributions"],
    }),

    getContribution: builder.query<UserContribution, string>({
      query: (id) => `/contribute/${id}`,
    }),

    // public "Shipped Ideas" for a member's profile.
    getShippedByUser: builder.query<{ shipped: ShippedIdea[] }, string>({
      query: (username) => `/contribute/shipped/${encodeURIComponent(username)}`,
    }),

    // ── admin ──
    getAdminContributions: builder.query<{ contributions: AdminContributionListItem[] }, string | void>({
      query: (status) =>
        status ? `/admin/contributions?status=${encodeURIComponent(status)}` : "/admin/contributions",
      providesTags: ["AdminContributions"],
    }),

    getAdminContribution: builder.query<AdminContributionDetail, string>({
      query: (id) => `/admin/contributions/${id}`,
      providesTags: (_r, _e, id) => [{ type: "AdminContributions", id }],
    }),

    updateAdminContribution: builder.mutation<
      AdminContributionDetail,
      { id: string; status?: ContributionStatus; adminNotes?: string; shipped?: { featureTitle?: string; featureUrl?: string } }
    >({
      query: ({ id, ...body }) => ({ url: `/admin/contributions/${id}`, method: "PATCH", body }),
      invalidatesTags: (_r, _e, { id }) => ["AdminContributions", { type: "AdminContributions", id }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useStartContributionMutation,
  useSendContributionMessageMutation,
  useSendContributionVoiceMutation,
  useSubmitContributionMutation,
  useGetMyContributionsQuery,
  useGetContributionQuery,
  useGetShippedByUserQuery,
  useGetAdminContributionsQuery,
  useGetAdminContributionQuery,
  useUpdateAdminContributionMutation,
} = contributeApi;
